<template>
  <el-container class="login-container" :class="{ 'is-mobile': isMobile }">
    <el-header>
      <h2>F&M数据库查询</h2>
    </el-header>
    <el-main>
      <el-form :model="loginForm" :rules="loginRules" ref="loginFormRef" label-width="80px" class="login-form">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="loginForm.username" :prefix-icon="User" />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input v-model="loginForm.password" :prefix-icon="Lock" type="password" show-password />
        </el-form-item>
        <el-form-item class="button-group">
          <el-button type="primary" @click="handleLogin" :loading="loading" class="login-btn">登录</el-button>
          <el-button type="success" @click="handleRegister" class="register-btn">注册</el-button>
        </el-form-item>
      </el-form>
    </el-main>

    <!-- 备案号 - 电脑端 -->
    <div v-if="!isMobile" class="desktop-icp">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
        粤ICP备20071086号
      </a>
    </div>

    <!-- 备案号 - 手机端 -->
    <div v-if="isMobile" class="mobile-icp">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
        粤ICP备20071086号
      </a>
    </div>

    <el-dialog v-model="showChangePasswordDialog" :title="isMobile ? '修改密码' : '修改密码'" 
      :width="isMobile ? '90%' : '400px'" class="custom-dialog" destroy-on-close append-to-body>
      <el-form :model="changePwdForm" :label-width="isMobile ? '70px' : '100px'">
        <el-form-item label="新密码">
          <el-input v-model="changePwdForm.newPwd" type="password" show-password placeholder="请输入新密码" />
        </el-form-item>
        <el-form-item label="确认密码">
          <el-input v-model="changePwdForm.confirmPwd" type="password" show-password placeholder="请再次输入新密码" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showChangePasswordDialog = false">取消</el-button>
          <el-button type="primary" @click="handleChangePassword">确认修改</el-button>
        </span>
      </template>
    </el-dialog>
  </el-container>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { ElMessage } from 'element-plus';
import { User, Lock } from '@element-plus/icons-vue';
import { userLogin, Post_setSomeOneDNS } from '../api/users';
import { useRouter } from 'vue-router';

export default {
  name: 'LoginView',
  setup() {
    const store = useStore();
    const router = useRouter();
    const isMobile = ref(window.innerWidth <= 768);
    const loading = ref(false);

    const showChangePasswordDialog = ref(false);
    const changePwdForm = ref({
      newPwd: '',
      confirmPwd: ''
    });

    const loginForm = ref({
      username: '',
      password: '',
    });

    const loginRules = {
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' },
      ],
    };

    const loginFormRef = ref(null);

    const handleResize = () => {
      isMobile.value = window.innerWidth <= 768;
    };

    onMounted(() => {
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
    });

    const handleLogin = () => {
      loginFormRef.value.validate((valid) => {
        if (valid) {
          loading.value = true;
          userLogin(loginForm.value)
            .then(response => {
              ElMessage.success('登录成功！');
              localStorage.setItem('authToken', response.data.token);
              store.commit('setUserData', response.data.userInfo);

              if (response.data.hasOwnProperty('LogFormat')) {
                store.commit('setLogFormat', response.data.LogFormat);
              }

              router.push({ path: '/' });
            })
            .catch(error => {
              if (error && error.status === 403) {
                ElMessage.warning('密码已过期，请更换密码！');
                showChangePasswordDialog.value = true;
              } else if (error && error.status === 401) {
                ElMessage.error('用户名或密码错误！');
              } else {
                ElMessage.error('登录失败，请检查输入或网络连接！');
              }
            })
            .finally(() => {
              loading.value = false;
            });
        } else {
          ElMessage.error('请填写完整的登录信息！');
          return false;
        }
      });
    };

    const handleChangePassword = () => {
      if (changePwdForm.value.newPwd.length < 6) {
        ElMessage.error('密码长度不能少于6位！');
        return;
      }

      const hasLetter = /[A-Za-z]/.test(changePwdForm.value.newPwd);
      const hasDigit = /\d/.test(changePwdForm.value.newPwd);
      if (!hasLetter || !hasDigit) {
        ElMessage.error('密码必须包含字母和数字！');
        return;
      }

      if (changePwdForm.value.newPwd !== changePwdForm.value.confirmPwd) {
        ElMessage.error('两次输入的密码不一致！');
        return;
      }

      const form = { username: loginForm.value.username, newPassword: changePwdForm.value.newPwd };

      Post_setSomeOneDNS(form, 'user/ChangeUserPassword')
        .then(() => {
          ElMessage.success('密码修改成功，请重新登录！');
          showChangePasswordDialog.value = false;
          changePwdForm.value = { newPwd: '', confirmPwd: '' };
        })
        .catch(error => {
          if (error && error.status === 403) {
            ElMessage.error('检测到为历史密码，修改失败！');
          } else if (error && error.status === 401) {
            ElMessage.error('用户不存在');
          } else {
            ElMessage.error('修改失败，请检查输入或网络连接！');
          }
        });
    };

    const handleRegister = () => {
      ElMessage.error('暂不支持！请联系管理员开通');
    };

    return {
      isMobile,
      loading,
      loginForm,
      loginRules,
      loginFormRef,
      handleLogin,
      handleRegister,
      showChangePasswordDialog, 
      changePwdForm, 
      handleChangePassword,
      User,
      Lock,
    };
  },
};    
</script>

<style scoped>
/* 电脑端样式 */
.login-container {
  width: 400px;
  margin: 100px auto 40px;
  padding: 30px 20px 20px;
  border: 1px solid #dcdfe6;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  background: linear-gradient(145deg, #ffffff 0%, #fafafa 100%);
  transition: all 0.3s ease;
  position: relative;
}

.login-container:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.el-header {
  text-align: center;
  margin-bottom: 30px;
}

.el-header h2 {
  font-size: 24px;
  color: #1e293b;
  font-weight: 600;
  margin: 0;
  position: relative;
  padding-bottom: 12px;
}

.el-header h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #409eff, #337ecc);
  border-radius: 2px;
}

.login-form {
  padding: 0 10px;
}

.button-group {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 20px;
}

.login-btn, .register-btn {
  flex: 1;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.login-btn {
  background: linear-gradient(135deg, #409eff 0%, #337ecc 100%);
  border: none;
  box-shadow: 0 4px 6px rgba(64, 158, 255, 0.2);
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 12px rgba(64, 158, 255, 0.3);
}

.register-btn {
  background: #ffffff;
  border: 1px solid #67c23a;
  color: #67c23a;
}

.register-btn:hover {
  background: #67c23a;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(103, 194, 58, 0.2);
}

/* 电脑端备案号 */
.desktop-icp {
  position: absolute;
  bottom: -30px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
}

.desktop-icp a {
  color: #94a3b8;
  text-decoration: none;
  transition: color 0.2s ease;
}

.desktop-icp a:hover {
  color: #409eff;
}

/* 弹窗样式 */
.custom-dialog :deep(.el-dialog) {
  border-radius: 16px;
}

.custom-dialog :deep(.el-dialog__header) {
  padding: 20px 24px;
  margin: 0;
  border-bottom: 1px solid #edf2f7;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px 16px 0 0;
}

.custom-dialog :deep(.el-dialog__title) {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}

.custom-dialog :deep(.el-dialog__body) {
  padding: 24px;
}

.custom-dialog :deep(.el-dialog__footer) {
  padding: 16px 24px 24px;
  border-top: 1px solid #edf2f7;
}

/* ========== 手机端样式 ========== */
@media screen and (max-width: 768px) {
  .login-container.is-mobile {
    width: 90%;
    margin: 60px auto 30px;
    padding: 24px 16px 20px;
    border-radius: 24px;
  }

  .login-container.is-mobile .el-header h2 {
    font-size: 22px;
    padding-bottom: 10px;
  }

  .login-container.is-mobile .el-header h2::after {
    width: 50px;
    height: 2px;
  }

  .login-container.is-mobile .login-form {
    padding: 0;
  }

  .login-container.is-mobile :deep(.el-form-item) {
    margin-bottom: 22px;
  }

  .login-container.is-mobile :deep(.el-form-item__label) {
    font-size: 14px;
    color: #475569;
    font-weight: 500;
  }

  .login-container.is-mobile :deep(.el-input__wrapper) {
    border-radius: 12px;
    padding: 4px 12px;
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    box-shadow: none;
  }

  .login-container.is-mobile :deep(.el-input__wrapper:hover),
  .login-container.is-mobile :deep(.el-input__wrapper.is-focus) {
    border-color: #409eff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
  }

  .login-container.is-mobile :deep(.el-input__inner) {
    font-size: 15px;
    height: 44px;
  }

  .login-container.is-mobile .button-group {
    flex-direction: column;
    gap: 12px;
    margin-top: 30px;
  }

  .login-container.is-mobile .login-btn,
  .login-container.is-mobile .register-btn {
    width: 100%;
    height: 48px;
    font-size: 16px;
    border-radius: 40px;
  }

  .login-container.is-mobile .register-btn {
    background: #ffffff;
    border: 2px solid #67c23a;
  }

  /* 手机端备案号 */
  .mobile-icp {
    margin-top: 30px;
    text-align: center;
    padding-top: 20px;
    border-top: 1px dashed #e2e8f0;
  }

  .mobile-icp a {
    font-size: 12px;
    color: #94a3b8;
    text-decoration: none;
    letter-spacing: 0.5px;
  }

  .mobile-icp a:active {
    color: #409eff;
  }

  /* 手机端弹窗优化 */
  .custom-dialog :deep(.el-dialog) {
    width: 90% !important;
    border-radius: 24px;
    margin: 20px auto;
  }

  .custom-dialog :deep(.el-dialog__header) {
    padding: 18px 20px;
  }

  .custom-dialog :deep(.el-dialog__title) {
    font-size: 16px;
  }

  .custom-dialog :deep(.el-dialog__body) {
    padding: 20px;
  }

  .custom-dialog :deep(.el-dialog__footer) {
    padding: 16px 20px 20px;
  }

  .custom-dialog :deep(.el-form-item) {
    margin-bottom: 20px;
  }

  .custom-dialog :deep(.el-form-item__label) {
    font-size: 14px;
    color: #475569;
  }

  .custom-dialog :deep(.el-input__wrapper) {
    border-radius: 12px;
  }
}

/* 动画效果 */
.login-container {
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 滚动条美化 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}
</style>